"use client";

import React from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap } from "lucide-react";

interface BrandCardProps {
    title: string;
    description: string;
    href: string;
    className?: string;
    variant?: "default" | "youtube" | "tech";
    target?: React.HTMLAttributeAnchorTarget;
}

export function BrandCard({ title, description, href, className, variant = "default", target }: BrandCardProps) {
    return (
        <Link
            href={href}
            target={target}
            className={cn(
                "group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-zinc-200/90 bg-white dark:border-zinc-800/50 dark:bg-zinc-950 transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20",
                className
            )}
        >
            {/* Abstract Background */}
            <div className={cn(
                "relative h-[220px] w-full overflow-hidden",
                (variant === "tech" || variant === "youtube") ? "bg-white dark:bg-zinc-950" : "bg-zinc-50 dark:bg-zinc-900/50"
            )}>
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay" />

                {/* Grid - Only for default variant */}
                {variant === "default" && (
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-size-[24px_24px]" />
                )}

                {/* Animated Elements - Background blobs */}
                <motion.div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-primary/20 rounded-full blur-[60px]"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.6, 0.3]
                    }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* Content Area */}
                <div className={cn(
                    "absolute inset-0 flex items-center justify-center perspective-[1000px]",
                    (variant === "tech" || variant === "youtube") ? "p-0" : "p-4"
                )}>
                    {variant === "tech" ? (
                        // 3D Printer Variant - No "Window" Frame, Direct Animation
                        <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                            {/* Minimal Grid Background for Tech */}
                            <div className="absolute inset-0 bg-[linear-gradient(to_right,#06b6d408_1px,transparent_1px),linear-gradient(to_bottom,#06b6d408_1px,transparent_1px)] bg-size-[20px_20px]" />

                            {/* The 3D Printer */}
                            <div className="relative w-48 h-48 flex items-end justify-center mb-4">
                                {/* Printer Frame - Main Structure */}
                                <div className="absolute bottom-0 w-40 h-2 bg-zinc-800 dark:bg-zinc-700 rounded-sm z-10" /> {/* Base */}
                                <div className="absolute bottom-2 left-2 w-2 h-40 bg-zinc-700 dark:bg-zinc-600 rounded-t-sm z-0" /> {/* Left Pillar */}
                                <div className="absolute bottom-2 right-2 w-2 h-40 bg-zinc-700 dark:bg-zinc-600 rounded-t-sm z-0" /> {/* Right Pillar */}
                                <div className="absolute bottom-40 left-2 right-2 h-2 bg-zinc-700 dark:bg-zinc-600 z-0" /> {/* Top Bar */}

                                {/* Z-Axis Screw (Decorative) */}
                                <div className="absolute bottom-2 left-5 w-1 h-36 bg-zinc-300/20 dark:bg-zinc-500/20 rounded-full" />

                                {/* X-Axis Gantry (Moves Up) */}
                                <motion.div
                                    animate={{ y: [0, -40, 0] }}
                                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                                    className="absolute bottom-8 left-2 right-2 h-3 bg-zinc-800 dark:bg-zinc-700 flex items-center px-4 z-20 shadow-md"
                                >
                                    {/* Print Head (Moves Left/Right) */}
                                    <motion.div
                                        animate={{ x: [-20, 20, -20] }}
                                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                        className="relative w-8 h-5 bg-cyan-600 rounded-sm flex justify-center"
                                    >
                                        <div className="absolute -bottom-1 w-1 h-2 bg-cyan-400 rounded-full" /> {/* Nozzle */}
                                        <div className="absolute top-1 w-6 h-1 bg-white/20 rounded-full" /> {/* Detail */}

                                        {/* Fan Animation */}
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 0.5, repeat: Infinity, ease: "linear" }}
                                            className="absolute top-1.5 w-3 h-3 border border-white/30 rounded-full border-t-white/80"
                                        />
                                    </motion.div>
                                </motion.div>

                                {/* Print Bed (Moves Y - simulated by perspective/scale or just static for 2D view) */}
                                <div className="absolute bottom-4 w-28 h-2 bg-zinc-900/50 rounded-sm mx-auto border-t border-white/10" />

                                {/* The Object Being Printed */}
                                <motion.div
                                    animate={{
                                        height: [0, 40, 0],
                                        opacity: [0.8, 1, 0]
                                    }}
                                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                                    className="absolute bottom-6 w-16 bg-cyan-500/80 rounded-sm mx-auto border border-cyan-400/50 backdrop-blur-sm z-10"
                                    style={{
                                        boxShadow: "0 0 20px rgba(6, 182, 212, 0.3)"
                                    }}
                                >
                                    {/* Layer lines effect */}
                                    <div className="w-full h-full bg-[linear-gradient(to_bottom,transparent_2px,rgba(0,0,0,0.1)_3px)] bg-size-[100%_3px]" />
                                </motion.div>

                                {/* Particles/Sparks */}
                                {[...Array(5)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        animate={{
                                            y: [0, -30],
                                            x: [0, (Math.random() - 0.5) * 40],
                                            opacity: [1, 0],
                                            scale: [1, 0]
                                        }}
                                        transition={{
                                            duration: 1 + Math.random(),
                                            repeat: Infinity,
                                            delay: Math.random() * 2,
                                            ease: "easeOut"
                                        }}
                                        className="absolute bottom-6 left-1/2 w-1 h-1 bg-cyan-300 rounded-full shadow-[0_0_5px_rgba(6,182,212,0.8)]"
                                    />
                                ))}
                            </div>
                        </div>
                    ) : variant === "youtube" ? (
                        // YouTube Variant - Custom Artistic Animation
                        <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                            {/* Minimal Grid Background for YouTube (Red tint) */}
                            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ef444408_1px,transparent_1px),linear-gradient(to_bottom,#ef444408_1px,transparent_1px)] bg-size-[20px_20px]" />

                            {/* Central Play Button Pulse */}
                            <motion.div
                                animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.3, 0.1] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute w-32 h-32 bg-red-500/20 rounded-full blur-xl"
                            />

                            {/* Main Play Button */}
                            <motion.div
                                whileHover={{ scale: 1.1 }}
                                className="relative z-10 w-20 h-14 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/30 group-hover:shadow-red-500/50 transition-shadow"
                            >
                                <div className="w-0 h-0 border-t-8 border-t-transparent border-l-[14px] border-l-white border-b-8 border-b-transparent ml-1" />
                            </motion.div>

                            {/* Floating Elements (Views, Likes, Subs) */}
                            {[...Array(6)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    animate={{
                                        y: [0, -40, 0],
                                        x: [0, (i % 2 === 0 ? 10 : -10)],
                                        opacity: [0, 1, 0],
                                        scale: [0.5, 1, 0.5]
                                    }}
                                    transition={{
                                        duration: 3 + Math.random() * 2,
                                        repeat: Infinity,
                                        delay: i * 0.8,
                                        ease: "easeInOut"
                                    }}
                                    className="absolute"
                                    style={{
                                        left: `${20 + Math.random() * 60}%`,
                                        top: `${40 + Math.random() * 40}%`
                                    }}
                                >
                                    {i % 3 === 0 ? (
                                        // Heart
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-red-400/60">
                                            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                                        </svg>
                                    ) : i % 3 === 1 ? (
                                        // Thumbs Up
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-zinc-400/60">
                                            <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-1.91l-.01-.01L23 10z" />
                                        </svg>
                                    ) : (
                                        // Play Circle
                                        <div className="w-3 h-3 rounded-full border-2 border-red-400/40" />
                                    )}
                                </motion.div>
                            ))}

                            {/* Waveform Effect at bottom */}
                            <div className="absolute bottom-0 left-0 right-0 h-16 flex items-end justify-center gap-1 opacity-30">
                                {[...Array(20)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        animate={{ height: [10, 40, 10] }}
                                        transition={{
                                            duration: 1.5,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                            delay: i * 0.05
                                        }}
                                        className="flex-1 bg-red-500/50 rounded-t-sm"
                                    />
                                ))}
                            </div>

                            {/* Progress Bar Line */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-500/20">
                                <motion.div
                                    className="h-full bg-red-500"
                                    animate={{ width: ["0%", "100%"] }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                                />
                            </div>
                        </div>
                    ) : (
                        // Default Variant UI (Window Frame)
                        <motion.div
                            className="relative w-full max-w-[220px] aspect-[1.6/1] rounded-xl border border-white/40 bg-white/20 backdrop-blur-md shadow-2xl overflow-hidden dark:border-white/10 dark:bg-white/5"
                            whileHover={{
                                scale: 1.02,
                                rotateX: 2,
                                rotateY: -2,
                                transition: { duration: 0.3 }
                            }}
                            initial={{ rotateX: 0, rotateY: 0 }}
                            style={{ transformStyle: "preserve-3d" }}
                        >
                            {/* Mock UI Header */}
                            <div className="h-8 w-full border-b border-white/10 bg-white/10 flex items-center px-3 gap-1.5">
                                <div className="h-2 w-2 rounded-full bg-red-400/80" />
                                <div className="h-2 w-2 rounded-full bg-yellow-400/80" />
                                <div className="h-2 w-2 rounded-full bg-green-400/80" />
                            </div>
                            {/* Mock UI Body */}
                            <div className="p-4 space-y-3">
                                <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/10">
                                        <Zap className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="h-2.5 w-20 rounded-full bg-white/20" />
                                </div>
                                <div className="space-y-1.5">
                                    <div className="h-2 w-full rounded-full bg-white/10" />
                                    <div className="h-2 w-3/4 rounded-full bg-white/10" />
                                </div>
                                <div className="flex gap-1.5 pt-2 items-end h-12">
                                    {[40, 70, 50, 90, 60, 80].map((h, i) => (
                                        <div key={i} className="flex-1 rounded-sm bg-primary/40 hover:bg-primary/60 transition-colors" style={{ height: `${h}%` }} />
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Content Section */}
            <div className="flex flex-1 flex-col justify-between p-6 bg-white dark:bg-zinc-950 border-t border-zinc-100 dark:border-zinc-900">
                <div>
                    <div className="flex items-start justify-between gap-4">
                        <h3 className="font-syne text-xl font-bold uppercase tracking-tight text-foreground group-hover:text-primary transition-colors">
                            {title}
                        </h3>
                        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50 text-zinc-400 transition-all duration-300 group-hover:border-primary/20 group-hover:bg-primary/5 group-hover:text-primary dark:border-zinc-800 dark:bg-zinc-900">
                            <ArrowUpRight className="h-4 w-4" />
                        </span>
                    </div>
                    <p className="mt-3 font-inter text-sm leading-relaxed text-muted-foreground line-clamp-2">
                        {description}
                    </p>
                </div>

                <div className="mt-6 flex items-center gap-3">
                    <div className="flex -space-x-2">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-6 w-6 rounded-full border-2 border-white dark:border-zinc-950 bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center overflow-hidden">
                                <div className="w-full h-full bg-linear-to-br from-zinc-200 to-zinc-300 dark:from-zinc-800 dark:to-zinc-700" />
                            </div>
                        ))}
                    </div>
                    <span className="text-xs font-medium text-muted-foreground">{variant === "youtube" ? "Trusted by creators" : "Trusted by everyone"}</span>
                </div>
            </div>
        </Link>
    );
}
